import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Coins,
  ChevronDown,
  MapPin,
  Upload,
  X,
  Loader2,
  ImageIcon,
  AlertCircle,
  CheckCircle,
  Tag,
  Check,
} from 'lucide-react';
import { LucideIcon } from '../components/LucideIcon';
import {
  useGetCategoryTreeQuery,
  useGetCitiesQuery,
  useGetCreditConfigQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
  useGetPresignedUrlsMutation,
  useAppSelector,
  selectAuthWallet,
  useGetMyAdsQuery,
} from '@nimbazaar/store';
import { useToast } from '../components/Toast';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  publicUrl?: string;
  error?: string;
}

interface FormErrors {
  title?: string;
  description?: string;
  category?: string;
  budget?: string;
  images?: string;
}

export function PostAd() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id?: string }>();
  const isEditMode = !!editId;
  const toast = useToast();
  const wallet = useAppSelector(selectAuthWallet);
  const { data: treeData, isLoading: isCategoriesLoading } = useGetCategoryTreeQuery();
  const { data: myAdsData } = useGetMyAdsQuery(undefined, { skip: !isEditMode });
  const [createAd, { isLoading: isCreating }] = useCreateAdMutation();
  const [updateAd, { isLoading: isUpdating }] = useUpdateAdMutation();
  const [getPresignedUrls] = useGetPresignedUrlsMutation();
  const [editLoaded, setEditLoaded] = useState(false);

  const categories = treeData?.data ?? [];
  const { data: citiesData, isLoading: isCitiesLoading } = useGetCitiesQuery();
  const { data: creditConfigData } = useGetCreditConfigQuery();
  const AD_COST = creditConfigData?.data?.AD_CREATION_COST ?? 10;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const subCategoryDropdownRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(e.target as Node)) {
        setIsSubCategoryOpen(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target as Node)) {
        setIsLocationOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [subCategoryId, setSubCategoryId] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [location, setLocation] = useState('');
  const [images, setImages] = useState<ImageFile[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const balance = wallet?.balance ?? 0;

  const cityList = citiesData?.data ?? [];
  const filteredCities = cityList.filter((city) =>
    city.name.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const formatAmount = (value: string) => {
    const num = Number(value);
    if (!num || num <= 0) return '';
    if (num >= 1_00_00_000) return `Rs ${(num / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')} Crore`;
    if (num >= 1_00_000) return `Rs ${(num / 1_00_000).toFixed(2).replace(/\.?0+$/, '')} Lac`;
    if (num >= 1_000) return `Rs ${(num / 1_000).toFixed(2).replace(/\.?0+$/, '')} Thousand`;
    return `Rs ${num}`;
  };
  const hasEnoughCredits = balance >= AD_COST;

  // Populate form when editing
  useEffect(() => {
    if (!isEditMode || editLoaded || !myAdsData?.data || !categories.length) return;
    const ad = myAdsData.data.find((a) => a.id === editId);
    if (!ad) return;

    setTitle(ad.title);
    setDescription(ad.description);
    setLocation(ad.location || '');
    setMinBudget(ad.minBudget ? String(ad.minBudget) : '');
    setMaxBudget(ad.maxBudget ? String(ad.maxBudget) : '');

    // Find parent category - could be the ad's category or its parent
    const directCat = categories.find((c) => c.id === ad.category.id);
    if (directCat) {
      // Ad category is a parent category
      setParentCategoryId(directCat.id);
    } else {
      // Ad category is a subcategory, find its parent
      for (const parent of categories) {
        const sub = parent.children?.find((c: { id: string }) => c.id === ad.category.id);
        if (sub) {
          setParentCategoryId(parent.id);
          setSubCategoryId(ad.category.id);
          break;
        }
      }
    }

    // Load existing images
    if (ad.images?.length) {
      setImages(ad.images.map((img) => ({
        id: img.id,
        file: new File([], ''),
        preview: img.url,
        status: 'done' as const,
        publicUrl: img.url,
      })));
    }

    setEditLoaded(true);
  }, [isEditMode, editId, editLoaded, myAdsData, categories]);

  // Get subcategories for selected parent
  const selectedParent = categories.find((c) => c.id === parentCategoryId);
  const subcategories = selectedParent?.children ?? [];

  // Resolved category ID: subcategory if selected, else parent (if parent has no children)
  const resolvedCategoryId = subCategoryId || (selectedParent && subcategories.length === 0 ? parentCategoryId : '');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!title.trim() || title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (title.trim().length > 100) {
      newErrors.title = 'Title must be under 100 characters';
    }

    if (!description.trim() || description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (description.trim().length > 2000) {
      newErrors.description = 'Description must be under 2000 characters';
    }

    if (!resolvedCategoryId) {
      newErrors.category = 'Please select a category';
    }

    const min = minBudget ? Number(minBudget) : 0;
    const max = maxBudget ? Number(maxBudget) : 0;
    if (min && max && min > max) {
      newErrors.budget = 'Minimum budget cannot exceed maximum';
    }
    if ((min && min < 0) || (max && max < 0)) {
      newErrors.budget = 'Budget cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error('Limit Reached', `Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const newImages: ImageFile[] = [];
    const fileArray = Array.from(files).slice(0, remaining);

    for (const file of fileArray) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error('Invalid File', `${file.name} is not a supported image format`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File Too Large', `${file.name} exceeds 5MB limit`);
        continue;
      }
      newImages.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
      });
    }

    setImages((prev) => [...prev, ...newImages]);
  }, [images.length, toast]);

  const removeImage = (id: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const uploadImages = async (): Promise<string[]> => {
    // Split into already-done and pending
    const alreadyDone = images.filter((img) => img.status === 'done' && img.publicUrl);
    const pending = images.filter((img) => img.status !== 'done' || !img.publicUrl);

    if (pending.length === 0) {
      return alreadyDone.map((img) => img.publicUrl!);
    }

    // Mark all pending as uploading
    setImages((prev) =>
      prev.map((item) =>
        pending.some((p) => p.id === item.id) ? { ...item, status: 'uploading' } : item
      )
    );

    // 1. Get all presigned URLs in a single batch request
    const { data: presignedData } = await getPresignedUrls({
      files: pending.map((img) => ({
        filename: img.file.name,
        contentType: img.file.type,
        size: img.file.size,
      })),
      folder: 'ads',
    }).unwrap();

    // 2. Upload all files to S3 in parallel
    const uploadResults = await Promise.allSettled(
      pending.map(async (img, index) => {
        const presigned = presignedData[index];
        const uploadRes = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: img.file,
          headers: { 'Content-Type': img.file.type },
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
        return { id: img.id, publicUrl: presigned.publicUrl };
      })
    );

    // 3. Update image statuses
    const successUrls: string[] = [];
    const statusUpdates: Record<string, { status: ImageFile['status']; publicUrl?: string; error?: string }> = {};

    for (const result of uploadResults) {
      if (result.status === 'fulfilled') {
        statusUpdates[result.value.id] = { status: 'done', publicUrl: result.value.publicUrl };
        successUrls.push(result.value.publicUrl);
      } else {
        const failedImg = pending[uploadResults.indexOf(result)];
        statusUpdates[failedImg.id] = { status: 'error', error: 'Upload failed' };
      }
    }

    setImages((prev) =>
      prev.map((item) =>
        statusUpdates[item.id]
          ? { ...item, ...statusUpdates[item.id] }
          : item
      )
    );

    const failedCount = uploadResults.filter((r) => r.status === 'rejected').length;
    if (failedCount > 0) {
      throw new Error(`Failed to upload ${failedCount} image(s)`);
    }

    return [...alreadyDone.map((img) => img.publicUrl!), ...successUrls];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!isEditMode && !hasEnoughCredits) {
      toast.error('Insufficient Credits', `You need ${AD_COST} credits to post an ad. You have ${balance}.`);
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload images first
      let imageUrls: string[] = [];
      if (images.length > 0) {
        imageUrls = await uploadImages();
      }

      if (isEditMode) {
        // Update ad
        await updateAd({
          id: editId,
          title: title.trim(),
          description: description.trim(),
          categoryId: resolvedCategoryId,
          minBudget: minBudget ? Number(minBudget) : null,
          maxBudget: maxBudget ? Number(maxBudget) : null,
          location: location.trim() || null,
          images: imageUrls,
        }).unwrap();

        toast.success('Ad Updated', 'Your ad has been updated successfully!');
        navigate(-1);
      } else {
        // Create ad
        const result = await createAd({
          title: title.trim(),
          description: description.trim(),
          categoryId: resolvedCategoryId,
          minBudget: minBudget ? Number(minBudget) : undefined,
          maxBudget: maxBudget ? Number(maxBudget) : undefined,
          location: location.trim() || undefined,
          images: imageUrls.length > 0 ? imageUrls : undefined,
        }).unwrap();

        toast.success('Ad Created', 'Your ad has been posted successfully!');
        navigate(`/ad/${result.data.slug}`);
      }
    } catch (error) {
      const err = error as { status?: number; data?: { error?: { message?: string; code?: string } } };
      if (err?.status === 402) {
        toast.error('Insufficient Credits', 'You don\'t have enough credits to post an ad.');
      } else if (err?.status === 403) {
        toast.error('Forbidden', err?.data?.error?.message || 'You are not allowed to post ads.');
      } else {
        toast.error('Error', err?.data?.error?.message || `Failed to ${isEditMode ? 'update' : 'create'} ad. Please try again.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      handleImageSelect(e.dataTransfer.files);
    },
    [handleImageSelect]
  );

  return (
    <div className="bg-gray-50 min-h-screen py-4 sm:py-8">
      <div className="max-w-3xl mx-auto px-3 sm:px-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">{isEditMode ? 'Edit Ad' : 'Post a Wanted Ad'}</h1>

        {/* Credits Banner - only for new ads */}
        {!isEditMode && (
          <div
            className={`rounded-xl p-4 mb-6 flex items-center gap-3 ${
              hasEnoughCredits
                ? 'bg-primary-50 border border-primary-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                hasEnoughCredits ? 'bg-primary-100' : 'bg-red-100'
              }`}
            >
              <Coins className={`w-5 h-5 ${hasEnoughCredits ? 'text-primary-600' : 'text-red-600'}`} />
            </div>
            <div className="flex-1">
              <p className={`font-medium ${hasEnoughCredits ? 'text-primary-900' : 'text-red-900'}`}>
                Posting costs {AD_COST} credits
              </p>
              <p className={`text-sm ${hasEnoughCredits ? 'text-primary-600' : 'text-red-600'}`}>
                Your balance: {balance} credits
                {!hasEnoughCredits && ' — Not enough credits'}
              </p>
            </div>
            {!hasEnoughCredits && (
              <button
                onClick={() => navigate('/account')}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Buy Credits
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Category</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Parent Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category
                </label>
                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsSubCategoryOpen(false); }}
                    disabled={isCategoriesLoading}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 pr-10 border rounded-lg text-sm bg-white text-left transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                      isCategoryOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {parentCategoryId ? (
                      <>
                        <div className="w-6 h-6 rounded-md bg-primary-50 flex items-center justify-center shrink-0">
                          <LucideIcon
                            name={selectedParent?.icon || ''}
                            className="w-3.5 h-3.5 text-primary-500"
                            fallback={<Tag className="w-3.5 h-3.5 text-primary-500" />}
                          />
                        </div>
                        <span className="text-gray-900 truncate">{selectedParent?.name}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">Select category</span>
                    )}
                  </button>
                  <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />

                  {isCategoryOpen && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                      <div className="max-h-64 overflow-y-auto py-1">
                        {categories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              setParentCategoryId(cat.id);
                              setSubCategoryId('');
                              setIsCategoryOpen(false);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                              parentCategoryId === cat.id
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                              parentCategoryId === cat.id ? 'bg-primary-100' : 'bg-gray-100'
                            }`}>
                              <LucideIcon
                                name={cat.icon || ''}
                                className={`w-4 h-4 ${parentCategoryId === cat.id ? 'text-primary-500' : 'text-gray-400'}`}
                                fallback={<Tag className={`w-4 h-4 ${parentCategoryId === cat.id ? 'text-primary-500' : 'text-gray-400'}`} />}
                              />
                            </div>
                            <span className="flex-1 text-left truncate">{cat.name}</span>
                            {parentCategoryId === cat.id && (
                              <Check className="w-4 h-4 text-primary-500 shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subcategory */}
              {subcategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Subcategory
                  </label>
                  <div className="relative" ref={subCategoryDropdownRef}>
                    {(() => {
                      const selectedSub = subcategories.find((s) => s.id === subCategoryId);
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => { setIsSubCategoryOpen(!isSubCategoryOpen); setIsCategoryOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 pr-10 border rounded-lg text-sm bg-white text-left transition-all ${
                              isSubCategoryOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {subCategoryId ? (
                              <>
                                <div className="w-6 h-6 rounded-md bg-secondary-50 flex items-center justify-center shrink-0">
                                  <LucideIcon
                                    name={selectedSub?.icon || ''}
                                    className="w-3.5 h-3.5 text-secondary-500"
                                    fallback={<Tag className="w-3.5 h-3.5 text-secondary-500" />}
                                  />
                                </div>
                                <span className="text-gray-900 truncate">{selectedSub?.name}</span>
                              </>
                            ) : (
                              <span className="text-gray-400">Select subcategory</span>
                            )}
                          </button>
                          <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none transition-transform ${isSubCategoryOpen ? 'rotate-180' : ''}`} />

                          {isSubCategoryOpen && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                              <div className="max-h-64 overflow-y-auto py-1">
                                {subcategories.map((sub) => (
                                  <button
                                    key={sub.id}
                                    type="button"
                                    onClick={() => {
                                      setSubCategoryId(sub.id);
                                      setIsSubCategoryOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                                      subCategoryId === sub.id
                                        ? 'bg-secondary-50 text-secondary-700'
                                        : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                      subCategoryId === sub.id ? 'bg-secondary-100' : 'bg-gray-100'
                                    }`}>
                                      <LucideIcon
                                        name={sub.icon || ''}
                                        className={`w-4 h-4 ${subCategoryId === sub.id ? 'text-secondary-500' : 'text-gray-400'}`}
                                        fallback={<Tag className={`w-4 h-4 ${subCategoryId === sub.id ? 'text-secondary-500' : 'text-gray-400'}`} />}
                                      />
                                    </div>
                                    <span className="flex-1 text-left truncate">{sub.name}</span>
                                    {subCategoryId === sub.id && (
                                      <Check className="w-4 h-4 text-secondary-500 shrink-0" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {errors.category && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.category}
              </p>
            )}
          </div>

          {/* Title & Description */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Details</h2>

            {/* Title */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <span className={`text-xs ${title.length > 100 ? 'text-red-500' : 'text-gray-400'}`}>
                  {title.length}/100
                </span>
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What are you looking for?"
                maxLength={100}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <span className={`text-xs ${description.length > 2000 ? 'text-red-500' : 'text-gray-400'}`}>
                  {description.length}/2000
                </span>
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you need in detail — condition, specifications, preferred brand, etc."
                rows={5}
                maxLength={2000}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.description}
                </p>
              )}
            </div>
          </div>

          {/* Budget & Location */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Budget & Location</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Min Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Min Budget (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                    Rs.
                  </span>
                  <input
                    type="number"
                    value={minBudget}
                    onChange={(e) => setMinBudget(e.target.value)}
                    placeholder="0"
                    min="0"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                </div>
                {formatAmount(minBudget) && (
                  <p className="text-xs text-primary-600 mt-1">{formatAmount(minBudget)}</p>
                )}
              </div>

              {/* Max Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Max Budget (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                    Rs.
                  </span>
                  <input
                    type="number"
                    value={maxBudget}
                    onChange={(e) => setMaxBudget(e.target.value)}
                    placeholder="0"
                    min="0"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                </div>
                {formatAmount(maxBudget) && (
                  <p className="text-xs text-primary-600 mt-1">{formatAmount(maxBudget)}</p>
                )}
              </div>
            </div>
            {errors.budget && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.budget}
              </p>
            )}

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Location (optional)
              </label>
              <div className="relative" ref={locationDropdownRef}>
                <button
                  type="button"
                  onClick={() => { setIsLocationOpen(!isLocationOpen); setLocationSearch(''); }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 pr-10 border rounded-lg text-sm bg-white text-left transition-all ${
                    isLocationOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  {location ? (
                    <span className="text-gray-900 truncate">{location}</span>
                  ) : (
                    <span className="text-gray-400">Select city</span>
                  )}
                </button>
                <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none transition-transform ${isLocationOpen ? 'rotate-180' : ''}`} />

                {isLocationOpen && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        placeholder="Search city..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {isCitiesLoading ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading cities...
                        </div>
                      ) : filteredCities.length > 0 ? (
                        filteredCities.map((city) => (
                          <button
                            key={city.id}
                            type="button"
                            onClick={() => {
                              setLocation(city.name);
                              setIsLocationOpen(false);
                              setLocationSearch('');
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition-colors ${
                              location === city.name
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <MapPin className="w-4 h-4 shrink-0" />
                            <span>{city.name}</span>
                            {location === city.name && <Check className="w-4 h-4 ml-auto text-primary-500" />}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">No city found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Images (optional)
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Add up to {MAX_IMAGES} reference images. Max 5MB each (JPEG, PNG, WebP, GIF).
            </p>

            {/* Drop Zone */}
            {images.length < MAX_IMAGES && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 font-medium">
                  Drop images here or click to browse
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {images.length}/{MAX_IMAGES} images added
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={(e) => {
                handleImageSelect(e.target.files);
                e.target.value = '';
              }}
              className="hidden"
            />

            {/* Image Preview Grid */}
            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 mt-4">
                {images.map((img) => (
                  <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={img.preview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    {/* Status overlay */}
                    {img.status === 'uploading' && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                    {img.status === 'done' && (
                      <div className="absolute top-1 right-1">
                        <CheckCircle className="w-5 h-5 text-green-500 drop-shadow" />
                      </div>
                    )}
                    {img.status === 'error' && (
                      <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-500" />
                      </div>
                    )}
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute top-1 left-1 w-6 h-6 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {errors.images && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.images}
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm text-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isCreating || isUpdating || (!isEditMode && !hasEnoughCredits)}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Posting...'}
                </>
              ) : (
                <>
                  <ImageIcon className="w-4 h-4" />
                  {isEditMode ? 'Save Changes' : `Post Ad (${AD_COST} credits)`}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
